import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {applyPendingIssueSuggestionsMutation as queryId} from '@github-ui/server-persisted-queries'
import type {ApplyPendingIssueSuggestionsMutationMutation} from '@github-ui/server-persisted-queries/types/applyPendingIssueSuggestionsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordProxy, type RecordSourceProxy} from 'relay-runtime'

import {
  type PendingSuggestionMutationOptions,
  pendingSuggestionOperation,
  type SuggestionRef,
  toSuggestionInput,
  updateRelayPendingSuggestions,
} from '../utils/suggestion-ref'
import {issueLifecyclePath} from './issue-lifecycle-mutation'

export type ApplySuggestionRef = SuggestionRef

type AppliedIssue = NonNullable<
  NonNullable<ApplyPendingIssueSuggestionsMutationMutation['applyPendingIssueSuggestions']>['issue']
>
type AssignedActor = NonNullable<NonNullable<AppliedIssue['assignedActors']['nodes']>[number]>
type ApplyPendingIssueSuggestionsResult = {
  data: ApplyPendingIssueSuggestionsMutationMutation
  errors: ReadonlyArray<{message?: string}>
}
type ApplyPendingIssueSuggestionsVariables = {
  operation: ReturnType<typeof pendingSuggestionOperation>
  actorId: string
  suggestions: SuggestionRef[]
  onApplied?: () => void
  onRollback?: () => void
}

function writeActor(store: RecordSourceProxy, actor: AssignedActor): RecordProxy {
  const record = store.get(actor.id) ?? store.create(actor.id, actor.__typename)
  record.setValue(actor.id, 'id')
  record.setValue(actor.login, 'login')
  record.setValue(actor.name, 'name')
  record.setValue(actor.profileResourcePath, 'profileResourcePath')
  record.setValue(actor.avatarUrl, 'avatarUrl', {size: 64})
  if (actor.__typename === 'Bot') {
    record.setValue(actor.isCopilot, 'isCopilot')
    record.setValue(actor.isAgent, 'isAgent')
    record.setValue(actor.displayName, 'displayName')
  }

  markActorType(store, actor.__typename)
  return record
}

function markActorType(store: RecordSourceProxy, typename: string) {
  const typeId = `client:__type:${typename}`
  const type = store.get(typeId) ?? store.create(typeId, '__TypeSchema')
  type.setValue(true, '__isActor')
  type.setValue(true, '__isNode')
}

function updateRelayAssignments(store: RecordSourceProxy, issue: AppliedIssue) {
  const issueRecord = store.get(issue.id)
  if (!issueRecord) return

  const assignedActors = issueRecord.getOrCreateLinkedRecord('assignedActors', 'ActorConnection', {first: 20})
  assignedActors.setLinkedRecords(
    issue.assignedActors.nodes?.map(actor => (actor ? writeActor(store, actor) : null)) ?? [],
    'nodes',
  )
  const assignments = issue.agentAssignments.map((assignment, index) => {
    const assignmentId = `client:${issue.id}:agentAssignments:${index}`
    const assignmentRecord = store.get(assignmentId) ?? store.create(assignmentId, 'AgentAssignment')

    if (assignment.assignee) {
      const assignee = store.get(assignment.assignee.id) ?? store.create(assignment.assignee.id, 'Bot')
      assignee.setValue(assignment.assignee.id, 'id')
      assignee.setValue(assignment.assignee.login, 'login')
      assignee.setValue(assignment.assignee.avatarUrl, 'avatarUrl', {size: 64})
      assignee.setValue(assignment.assignee.isCopilot, 'isCopilot')
      assignee.setValue(assignment.assignee.isAgent, 'isAgent')
      assignee.setValue(assignment.assignee.displayName, 'displayName')
      markActorType(store, 'Bot')
      assignmentRecord.setLinkedRecord(assignee, 'assignee')
    } else {
      assignmentRecord.setValue(null, 'assignee')
    }

    if (assignment.assigner) {
      const assigner =
        store.get(assignment.assigner.id) ?? store.create(assignment.assigner.id, assignment.assigner.__typename)
      assigner.setValue(assignment.assigner.id, 'id')
      assigner.setValue(assignment.assigner.login, 'login')
      markActorType(store, assignment.assigner.__typename)
      assignmentRecord.setLinkedRecord(assigner, 'assigner')
    } else {
      assignmentRecord.setValue(null, 'assigner')
    }

    return assignmentRecord
  })
  issueRecord.setLinkedRecords(assignments, 'agentAssignments')
}

export function useApplyPendingIssueSuggestions({
  issueId,
  owner,
  repository,
  issueNumber,
  onCompleted,
  onError,
}: PendingSuggestionMutationOptions<ApplyPendingIssueSuggestionsMutationMutation>) {
  const environment = useRelayEnvironment()
  const {mutate} = useMutation<
    ApplyPendingIssueSuggestionsResult,
    Error,
    ApplyPendingIssueSuggestionsVariables,
    Disposable
  >({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: async ({actorId, suggestions}) => {
      const path = `${issueLifecyclePath(owner, repository, issueNumber)}/pending_suggestions.json`
      const response = await reactFetchJSON(`${path}?${new URLSearchParams({queryId})}`, {
        method: 'PUT',
        body: {actorId, suggestions: suggestions.map(toSuggestionInput)},
      })
      if (!response.ok) throw new Error(`Could not accept suggestions (${response.status})`)
      const json = (await response.json()) as {
        data?: ApplyPendingIssueSuggestionsMutationMutation
        errors?: Array<{message?: string}>
      }
      // Relay completed responses containing data even with GraphQL errors.
      // In particular, apply can return confirmed assignments after a partial failure.
      if (!json.data) {
        throw new Error(json.errors?.[0]?.message ?? 'Could not accept suggestions')
      }
      return {data: json.data, errors: json.errors ?? []}
    },
    onMutate: ({operation, onApplied}) => {
      const optimisticUpdate = environment.applyUpdate({
        storeUpdater: store => updateRelayPendingSuggestions(store, operation),
      })
      onApplied?.()
      return optimisticUpdate
    },
    onSuccess: async ({data, errors}, {operation, onRollback}) => {
      const issue = data.applyPendingIssueSuggestions?.issue
      const error = errors.find(item => item.message)?.message
      const hasErrors = errors.length > 0

      if (issue) {
        await notifySettlement(
          {
            type: 'setFields',
            id: issue.id,
            fields: {assignedActors: issue.assignedActors, agentAssignments: issue.agentAssignments},
          },
          ENTITY_FIELDS_OWNER,
          DOCUMENT_FIELDS_PATH,
        )
        commitLocalUpdate(environment, store => updateRelayAssignments(store, issue))
      }

      if (!hasErrors && issue) {
        await notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
        commitLocalUpdate(environment, store => updateRelayPendingSuggestions(store, operation))
      }

      if (hasErrors || !issue) {
        onRollback?.()
        onError?.(new Error(error ?? 'Could not accept all suggestions'))
        return
      }
      onCompleted?.(data)
    },
    onError: (error, {onRollback}) => {
      onRollback?.()
      onError?.(error)
    },
    onSettled: (_data, _error, _variables, optimisticUpdate) => optimisticUpdate?.dispose(),
  })

  return useCallback(
    (
      actorId: string,
      suggestions: SuggestionRef[],
      onApplied?: () => void,
      additionalSuggestionIds?: string[],
      onRollback?: () => void,
    ) => {
      const operation = pendingSuggestionOperation(issueId, {
        suggestionIds: [...suggestions.map(suggestion => suggestion.id), ...(additionalSuggestionIds ?? [])],
        state: 'APPLIED',
      })
      mutate({operation, actorId, suggestions, onApplied, onRollback})
    },
    [issueId, mutate],
  )
}
